import { ReactNode, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { PeriodBucket } from '../../../utils/reunionMensuelle';
import { ReunionTransaction } from '../../../hooks/useReunionMensuelleData';
import SalesListModal from './SalesListModal';
import InfoTooltip from './InfoTooltip';

const BAR_COLORS = ['#6366f1', '#10b981', '#f43f5e', '#f59e0b', '#0ea5e9', '#a855f7', '#14b8a6', '#ec4899'];

interface StackedBarCardProps {
  icon: ReactNode;
  iconBg: string;
  title: string;
  periods: PeriodBucket[];
  categories: string[];
  data: Map<string, Map<string, number>>;
  getTransactions: (periodKey: string, categoryKey: string) => ReunionTransaction[];
  infoText?: string;
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((sum: number, p: any) => sum + (p.value || 0), 0);
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-lg px-3.5 py-2.5 text-xs min-w-[160px]">
      <p className="font-semibold text-slate-700 mb-1.5">{label}</p>
      <div className="space-y-1">
        {payload.filter((p: any) => p.value > 0).map((entry: any) => (
          <div key={entry.dataKey} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
            <span className="text-slate-500">{entry.name}</span>
            <span className="ml-auto font-semibold text-slate-800">{entry.value}</span>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-1 mt-1 border-t border-slate-100">
          <span className="text-slate-500">Total</span>
          <span className="ml-auto font-bold text-slate-800">{total}</span>
        </div>
      </div>
    </div>
  );
}

export default function StackedBarCard({ icon, iconBg, title, periods, categories, data, getTransactions, infoText }: StackedBarCardProps) {
  const [modalCell, setModalCell] = useState<{ periodKey: string; periodLabel: string; categoryKey: string } | null>(null);

  const chartData = periods.map(p => {
    const row: Record<string, any> = { period: p.label };
    categories.forEach(cat => {
      row[cat] = data.get(p.key)?.get(cat) ?? 0;
    });
    return row;
  });

  const totalsByPeriod = periods.map(p => {
    const row = data.get(p.key);
    return row ? Array.from(row.values()).reduce((sum, v) => sum + v, 0) : 0;
  });

  const handleCellClick = (periodKey: string, periodLabel: string, categoryKey: string) => {
    setModalCell({ periodKey, periodLabel, categoryKey });
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center shadow-sm`}>{icon}</div>
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        {infoText && <InfoTooltip text={infoText} />}
      </div>

      <div className="p-5 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }} barCategoryGap="22%">
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
            <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f1f5f9' }} />
            <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
            {categories.map((cat, i) => {
              const isLast = i === categories.length - 1;
              return (
                <Bar
                  key={cat}
                  dataKey={cat}
                  stackId="total"
                  fill={BAR_COLORS[i % BAR_COLORS.length]}
                  radius={isLast ? [6, 6, 0, 0] : 0}
                  cursor="pointer"
                  onClick={(_: any, index: number) => handleCellClick(periods[index].key, periods[index].label, cat)}
                />
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-x-auto border-t border-slate-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50">
              <th className="px-4 py-2 text-left font-semibold text-slate-500 text-xs uppercase tracking-wide">Période</th>
              {categories.map((cat, i) => (
                <th key={cat} className="px-4 py-2 text-right font-semibold text-xs uppercase tracking-wide">
                  <span className="inline-flex items-center gap-1.5 justify-end w-full">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }} />
                    <span className="text-slate-500">{cat}</span>
                  </span>
                </th>
              ))}
              <th className="px-4 py-2 text-right font-semibold text-slate-500 text-xs uppercase tracking-wide">Total</th>
            </tr>
          </thead>
          <tbody>
            {periods.map((p, idx) => (
              <tr key={p.key} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors">
                <td className="px-4 py-2 text-slate-600">{p.label}</td>
                {categories.map(cat => {
                  const count = data.get(p.key)?.get(cat) ?? 0;
                  return (
                    <td
                      key={cat}
                      onClick={count > 0 ? () => handleCellClick(p.key, p.label, cat) : undefined}
                      className={`px-4 py-2 text-right font-medium ${count > 0 ? 'text-indigo-600 cursor-pointer hover:underline' : 'text-slate-700'}`}
                    >
                      {count}
                    </td>
                  );
                })}
                <td className="px-4 py-2 text-right font-bold text-slate-800">{totalsByPeriod[idx]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalCell && (
        <SalesListModal
          title={`${title} — ${modalCell.periodLabel} — ${modalCell.categoryKey}`}
          transactions={getTransactions(modalCell.periodKey, modalCell.categoryKey)}
          onClose={() => setModalCell(null)}
        />
      )}
    </div>
  );
}
